import React, { Fragment, memo, useCallback } from 'react'
import tw from 'twin.macro'
import { Form, Formik } from 'formik'
import { Typography } from '@mui/material'
import { useSnackbar } from 'notistack'
import { useSelector } from 'react-redux'
import { Button } from 'src/components/shared/Button'
import { CommonModal } from 'src/components/shared/CommonModal'
import { DataRow } from 'src/components/shared/DataRow'
import { FormikCheckbox } from 'src/components/shared/form/formik/FormikCheckbox'
import { FormikTextArea } from 'src/components/shared/form/formik/FormikTextArea'
import { AdminAuthorizeTransaction } from 'src/constants/actionTypes'
import { transactionPaymentTypeLabels } from 'src/constants/payments-ops/labels'
import { useAsyncDispatchWithErrorToast } from 'src/hooks/useAsyncDispatchWithErrorToast'
import type { PaymentsOpsTransaction } from 'src/interfaces/transactionsReview'
import { getTransactionsReviewSubmitting } from 'src/selectors/transactionsReview'
import { formatCentsAsUsd } from 'src/utils/invoiceMoney'
import { usePartnerNameMap } from 'src/utils/hooks/paymentsOps/usePartnerNameMap'
import { resolvePartnerName } from 'src/utils/paymentsOps/partnerNames'
import { authorizeTransactionValidator } from './AuthorizeTransactionModal.validator'

interface Props {
  /** `null` closes the modal. */
  transaction: PaymentsOpsTransaction | null
  onClose(): void
}

interface AuthorizeFormValues {
  notes: string
  isConfirmed: boolean
}

const FORM_ID = 'authorize-transaction-form'

const INITIAL_VALUES: AuthorizeFormValues = { notes: '', isConfirmed: false }

const AuthorizeTransactionModalComponent = ({ transaction, onClose }: Props) => {
  const snackbar = useSnackbar()
  const { asyncDispatchWithErrorToast } = useAsyncDispatchWithErrorToast()
  const nameMap = usePartnerNameMap()
  const isSubmitting = useSelector(getTransactionsReviewSubmitting)

  const handleSubmit = useCallback(
    async (values: AuthorizeFormValues) => {
      if (!transaction) {
        return
      }

      const result = await asyncDispatchWithErrorToast(
        AdminAuthorizeTransaction.request({
          transactionId: transaction.id,
          notes: values.notes.trim(),
        }),
      )

      if (!result) {
        return
      }

      // The modal cannot be dismissed while the write is in flight, so it is still open here.
      snackbar?.enqueueSnackbar('Transaction authorized', { variant: 'success' })
      onClose()
    },
    [asyncDispatchWithErrorToast, onClose, snackbar, transaction],
  )

  if (!transaction) {
    return null
  }

  const amount = formatCentsAsUsd(transaction.amount)
  const partnerName = resolvePartnerName(nameMap, transaction.partnerId, transaction.partnerName)
  const paymentType = transaction.paymentType
    ? transactionPaymentTypeLabels[transaction.paymentType] ?? transaction.paymentType
    : '—'

  return (
    <Formik<AuthorizeFormValues>
      initialValues={INITIAL_VALUES}
      validationSchema={authorizeTransactionValidator}
      validateOnMount
      onSubmit={handleSubmit}
    >
      {({ isValid, isSubmitting: isFormSubmitting }) => {
        // Money is moving: neither Cancel, the backdrop nor Escape may close the modal until the
        // write has been reported. The Formik flag covers the gap before the store flag is set.
        const isBusy = isSubmitting || isFormSubmitting
        const handleClose = () => {
          if (isBusy) {
            return
          }

          onClose()
        }

        return (
          <CommonModal
            isOpen
            title="Authorize transaction"
            className={tw`max-w-[520px]`}
            onClose={handleClose}
            buttons={
              <Fragment>
                <Button size="small" color="grey" disabled={isBusy} onClick={handleClose}>
                  Cancel
                </Button>
                <Button
                  size="small"
                  color="dark"
                  type="submit"
                  form={FORM_ID}
                  loading={isBusy}
                  disabled={!isValid || isBusy}
                  data-testid="authorize-transaction-confirm"
                >
                  {`Authorize ${amount}`}
                </Button>
              </Fragment>
            }
          >
            <Form id={FORM_ID} css={tw`flex flex-col gap-4`}>
              <Typography variant="body2">
                Authorizing <b>{amount}</b> to <b>{partnerName}</b> via {paymentType} · effective{' '}
                <b>{transaction.effectiveDate ?? '—'}</b>. This debits the partner wallet by{' '}
                {amount}.
              </Typography>
              <div css={tw`flex flex-col gap-2 rounded-lg bg-shades-50 p-4`}>
                <DataRow title="Amount" text={amount} />
                <DataRow title="Partner" text={partnerName} />
                <DataRow title="Payment type" text={paymentType} />
                <DataRow title="External ID" text={transaction.externalTransactionId ?? '—'} />
              </div>
              <FormikTextArea<AuthorizeFormValues>
                name="notes"
                label="Authorization notes *"
                placeholder="At least 10 characters — recorded with the authorization"
                minRows={3}
                fullWidth
                disabled={isBusy}
                data-testid="authorize-transaction-notes"
              />
              <FormikCheckbox<AuthorizeFormValues>
                name="isConfirmed"
                label="I confirm the amount and destination are correct."
                disabled={isBusy}
                inputProps={{ 'data-testid': 'authorize-transaction-confirmed' }}
              />
            </Form>
          </CommonModal>
        )
      }}
    </Formik>
  )
}

export const AuthorizeTransactionModal = memo(AuthorizeTransactionModalComponent)
